import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorageIcon from '@mui/icons-material/Storage';
import CategoryIcon from '@mui/icons-material/Category';
import api from '../services/api';
import { getBackendBaseUrl } from '../utils/configUtils';
import { getBestBoxImageUrl } from '../utils/imageUtils';
import { colors, shadows } from '../styles/styleConstants';
import { AuthContext } from '../context/AuthContext';

// Import shared image cache
import { 
  normalizeUrlForCache, 
  addToCache, 
  isInCache 
} from '../utils/imageCache';

/**
 * BoxThumbnail component displays a thumbnail for a box
 * Simplified implementation without offline storage dependencies
 */
const BoxThumbnail = ({ boxId, size = 'medium', height = 150, borderRadius = 1, showPlaceholder = true }) => {
  // Get current user from AuthContext
  const { currentUser } = useContext(AuthContext);
  
  // State for the image URL and loading status
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  
  // Reference for component mounted state
  const isMounted = useRef(true);
  
  // Build the backend base URL once
  const backendBaseUrl = useMemo(() => getBackendBaseUrl(), []);
  
  // Generate the API URL with cache busting
  const apiUrl = useMemo(() => {
    // Adding a cache buster to avoid browser caching
    const cacheBuster = Date.now();
    // Don't include /api/v1 here, as api service adds it
    return `/boxes/${boxId}?_t=${cacheBuster}`;
  }, [boxId]);

  // Function to build image URL through backend proxy
  const buildImageUrl = useCallback((imageId, imageSize = 'medium') => {
    if (!imageId) {
      console.log('BoxThumbnail: Missing imageId, cannot build URL');
      return null;
    }
    
    if (!currentUser?.id) {
      console.log('BoxThumbnail: No current user ID available, cannot build URL');
      return null;
    }
    
    // Create a proper box object with the imageId
    const boxForImageUrl = {
      id: boxId,
      user_id: currentUser.id,
      custom_image_id: imageId,
      customImageId: imageId // Support both formats
    };
    
    const optimizedUrl = getBestBoxImageUrl(boxForImageUrl);
    
    console.log('BoxThumbnail: Using optimized box image URL:', optimizedUrl);
    return optimizedUrl;
  }, [boxId, currentUser?.id]);
  
  // Load the image once on component mount or when boxId/size changes
  useEffect(() => {
    // Function to load the box image
    const loadBoxImage = async () => {
      console.log(`BoxThumbnail: Loading image for box ${boxId}...`); 
      if (!boxId) {
        console.log('BoxThumbnail: No boxId provided, showing placeholder');
        setIsLoading(false);
        return;
      }
      
      if (!currentUser?.id) {
        console.log('BoxThumbnail: No current user available, waiting...');
        setIsLoading(false);
        return;
      }
      
      try {
        console.log(`BoxThumbnail: Fetching box data from API: ${apiUrl}`);
        
        // Fetch box data from API
        const response = await api.get(apiUrl);
        console.log(`BoxThumbnail: API response status for box ${boxId}:`, response.status);
        
        const box = response.data;
        console.log(`BoxThumbnail: Full API response data for box ${boxId}:`, box);
        
        if (!box) {
          console.error(`BoxThumbnail: No data returned for box ${boxId}`);
          setIsLoading(false);
          return;
        }
        
        // Get imageId from either camelCase or snake_case property
        const customImageId = box?.customImageId || box?.custom_image_id;
        
        // Log the box structure for debugging
        console.log(`BoxThumbnail: Box data structure for ${boxId}:`, {
          hasCustomImageId: !!customImageId,
          customImageId,
          snakeCaseExists: !!box?.custom_image_id,
          camelCaseExists: !!box?.customImageId,
          hasItems: Array.isArray(box?.items),
          itemCount: Array.isArray(box?.items) ? box.items.length : 0
        });
        
        //if (!isMounted.current) return;
        
        // Priority 1: Use custom image ID if available
        if (customImageId) {
          const directUrl = buildImageUrl(customImageId, size);
          const normalizedUrl = normalizeUrlForCache(directUrl);
          
          // Check if image is already in cache
          if (isInCache(directUrl)) {
            console.log(`BoxThumbnail: ✅ CACHE HIT for box ${boxId}: ${directUrl} (normalized: ${normalizedUrl})`);
          } else {
            console.log(`BoxThumbnail: ❌ CACHE MISS for box ${boxId}: ${normalizedUrl} - will download`);
          }
          
          console.log(`BoxThumbnail: Using custom image ID for box ${boxId}:`, directUrl);
          console.log(`BoxThumbnail: Setting imageUrl and isLoading=false for box ${boxId}`);
          setImageUrl(directUrl);
          setImageError(false);
          setIsLoading(false);
          
          // Force-log the state after a short delay to verify state updates
          setTimeout(() => {
            console.log(`BoxThumbnail: State AFTER update for box ${boxId}:`, {
              imageUrl: directUrl,
              isLoading: false,
              imageError: false
            });
          }, 100);
          return;
        }
        
        // Priority 2: Try to find an item with an image in the box
        if (box && box.items && box.items.length) {
          // Look for the first item with a storage_file_id
          for (const item of box.items) {
            if (item.storage_file_id) {
              const directUrl = buildImageUrl(item.storage_file_id, size);
              console.log(`BoxThumbnail: Using item image for box ${boxId}:`, directUrl);
              setImageUrl(directUrl);
              setImageError(false);
              setIsLoading(false);
              return;
            }
          }
        }
        
        // No image found
        console.log(`BoxThumbnail: No image found for box ${boxId}`);
        setImageUrl(null);
        setIsLoading(false);
      } catch (error) {
        // Log detailed error information
        console.error(`BoxThumbnail: Error fetching box ${boxId}:`, {
          error,
          message: error.message,
          response: error.response,
          status: error.response?.status,
          statusText: error.response?.statusText,
          apiUrl: `${apiUrl}/boxes/${boxId}`
        });
        console.error(`BoxThumbnail: Error loading box ${boxId}:`, error);
        if (isMounted.current) {
          setImageError(true);
          setIsLoading(false);
        }
      }
    };

    loadBoxImage();
    
    // Clean up function
    return () => {
      isMounted.current = false;
    };
  }, [boxId, size, apiUrl, buildImageUrl, backendBaseUrl, currentUser?.id]);

  // Handle image load error with more detailed logging
  const handleImageError = (e) => {
    console.error(`BoxThumbnail: Image load error for box ${boxId}:`, {
      url: imageUrl,
      event: e.type,
      target: e.target.src
    });
    setImageError(true);
  };

  // Debugging props
  console.log(`BoxThumbnail: Render state for box ${boxId}:`, {
    isLoading,
    hasImageUrl: !!imageUrl,
    imageError,
    height
  });
  
  // Generate unique placeholder icon based on boxId
  const getPlaceholderIcon = () => {
    if (!boxId) return <CategoryIcon sx={{ fontSize: height * 0.4 }} />;
    
    // Use last digit of boxId to determine which icon to show
    const iconType = parseInt(boxId.toString().slice(-1)) % 4;
    
    switch(iconType) {
      case 0:
        return <StorageIcon sx={{ fontSize: height * 0.4 }} />;
      case 1:
        return <InventoryIcon sx={{ fontSize: height * 0.4 }} />;
      case 2:
        return <LocalShippingIcon sx={{ fontSize: height * 0.4 }} />;
      default:
        return <CategoryIcon sx={{ fontSize: height * 0.4 }} />;
    }
  };

  // Generate placeholder background color based on boxId
  const getPlaceholderColor = () => {
    if (!boxId) return colors.darkSlateBlue;
    
    // Use first digit of boxId to determine background color
    const colorType = parseInt(boxId.toString()[0]) % 5;
    
    const placeholderColors = [
      '#3D485A', // darkSlateBlue
      '#5A7D7C', // slateBlue
      '#5E6C84', // mediumSlateBlue
      '#2D3748', // charcoal
      '#3B82F6', // actionBlue
    ];
    
    return placeholderColors[colorType];
  };

  // Render the component
  return (
    <Box 
      sx={{
        height: height,
        position: 'relative',
        backgroundColor: 'rgba(0,0,0,0.03)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: borderRadius,
        boxShadow: shadows.sm,
      }}
    >
      {isLoading ? (
        // Loading state - show box icon as placeholder during loading
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          backgroundColor: 'rgba(94, 108, 132, 0.08)'
        }}>
          <CategoryIcon 
            sx={{ 
              fontSize: height * 0.4,
              color: colors.mediumSlateBlue,
              opacity: 0.5,
              mb: 1
            }} 
          />
          <Typography variant="caption" sx={{ color: colors.mediumSlateBlue, opacity: 0.7 }}>
            Loading...
          </Typography>
        </Box>
      ) : imageUrl && !imageError ? (
        // Image loaded - show the image
        <Box 
          component="img"
          src={imageUrl}
          alt={`Box ${boxId}`}
          onError={handleImageError}
          onLoad={() => {
            console.log(`BoxThumbnail: Image successfully loaded for box ${boxId}:`, imageUrl);
            // Add to shared cache
            addToCache(imageUrl);
          }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        // Error or no image - show designed box placeholder
        <Box
          sx={{
            width: '100%',
            height: '100%',
            backgroundColor: getPlaceholderColor(),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            padding: 1,
            opacity: 0.9
          }}
        >
          {getPlaceholderIcon()}
          
          <Typography 
            variant="caption" 
            sx={{ 
              mt: 1, 
              fontWeight: 600,
              textAlign: 'center',
              maxWidth: '90%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              opacity: 0.9
            }}
          >
            Box {boxId || ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BoxThumbnail;

// Export the component with a display name for easier debugging
BoxThumbnail.displayName = 'BoxThumbnail';
